import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import {
    CrosshairInteractionMode,
    ToolManager,
} from '~/engine/tool/ToolManager';
import { vec2, vec3 } from 'gl-matrix';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ROI_POINT_TYPES } from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiSelectionRequestedEvent,
} from '~/domain/events';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import { formatColorRgb } from '~/utils/color';
import { selectedToolState } from '~/state/atoms/toolsAtoms';

/**
 * Maximum distance in mm from the current viewing plane at which a POI
 * is considered "on-plane" and rendered. This must be at least half the
 * largest expected slice spacing so markers that fall between two slices
 * remain visible on the nearest one (typical CT spacing: 1–5 mm).
 */
const ON_PLANE_THRESHOLD_MM = 2.5;
/** Radius in px for an on-plane POI marker */
const ON_PLANE_RADIUS_PX = 6;
/** Length of cross arms inside on-plane marker, in px */
const CROSS_ARM_PX = 3;

interface PointsOfInterestOverlayProps {
    view: PlaneView | null;
}

interface ProjectedPoi {
    roi: RoiReadModel;
    canvasX: number;
    canvasY: number;
    /** Distance from the current viewing plane in mm */
    distanceMm: number;
}

interface DragState {
    roiNumber: number;
    startMouse: { x: number; y: number };
    startPatientCoords: vec3;
}

/**
 * Projects point ROIs onto the current viewing plane.
 *
 * @param selectedRoiNumber - The selected ROI is always included
 *   regardless of distance from the plane so the user can see where
 *   it sits relative to the current slice.
 */
function computeProjectedPois(
    view: PlaneView,
    rois: ReadonlyArray<RoiReadModel>,
    selectedRoiNumber: number
): ProjectedPoi[] {
    const viewDir = view.viewDirection;
    const planeCoord = view.planeCoordinates;
    const results: ProjectedPoi[] = [];

    for (const roi of rois) {
        const pt = roi.pointCoordinatesMm;
        if (!pt) {
            continue;
        }

        const pointVec = vec3.fromValues(pt[0], pt[1], pt[2]);
        const distanceMm = Math.abs(vec3.dot(pointVec, viewDir) - planeCoord);

        // Always show the selected ROI; filter others by distance
        if (
            distanceMm >= ON_PLANE_THRESHOLD_MM &&
            roi.roiNumber !== selectedRoiNumber
        ) {
            continue;
        }

        const canvasCoords = view.canvasCoordsFromPatientCoords(pointVec);

        results.push({
            roi,
            canvasX: canvasCoords[0],
            canvasY: canvasCoords[1],
            distanceMm,
        });
    }

    return results;
}

const PointsOfInterestOverlay = ({ view }: PointsOfInterestOverlayProps) => {
    const structureSet = useRecoilValue(currentStructureSetState);
    const viewManager = useRecoilValue(viewManagerState);
    const selectedRoiNumber = useRecoilValue(selectedROIState);
    const selectedTool = useRecoilValue(selectedToolState);
    const poiMoveActive = selectedTool === 'poi-move';
    const [renderTick, setRenderTick] = useState(0);
    const dragRef = useRef<DragState | null>(null);
    const [dragOffset, setDragOffset] = useState<{
        roiNumber: number;
        dx: number;
        dy: number;
    } | null>(null);

    const forceUpdate = useCallback(() => {
        setRenderTick((prev) => prev + 1);
    }, []);

    // Sync POI move mode into ToolManager so Crosshairs reacts without
    // needing any POI-specific knowledge.
    useEffect(() => {
        ToolManager.setExternalCrosshairMode(
            poiMoveActive ? CrosshairInteractionMode.RootsOnly : null
        );
        return () => {
            ToolManager.setExternalCrosshairMode(null);
        };
    }, [poiMoveActive]);

    useEffect(() => {
        if (!viewManager) {
            return;
        }

        viewManager.addEventListener(
            'planeIntersectionPointChanged',
            forceUpdate
        );
        viewManager.addEventListener('zoomScaleChanged', forceUpdate);

        return () => {
            viewManager.removeEventListener(
                'planeIntersectionPointChanged',
                forceUpdate
            );
            viewManager.removeEventListener('zoomScaleChanged', forceUpdate);
        };
    }, [viewManager, forceUpdate]);

    const onDrag = useCallback((e: MouseEvent) => {
        if (!dragRef.current) {
            return;
        }
        const { startMouse } = dragRef.current;
        const dx = e.clientX - startMouse.x;
        const dy = e.clientY - startMouse.y;
        setDragOffset({
            roiNumber: dragRef.current.roiNumber,
            dx,
            dy,
        });
    }, []);

    const onEnd = useCallback(
        (e: MouseEvent) => {
            if (!dragRef.current || !view) {
                dragRef.current = null;
                setDragOffset(null);
                return;
            }

            const { startMouse, startPatientCoords, roiNumber } =
                dragRef.current;
            const dx = e.clientX - startMouse.x;
            const dy = e.clientY - startMouse.y;

            // Convert mouse positions to patient-space and compute delta.
            // clientToPatientCoordinates accounts for the canvas rect offset,
            // giving correct results regardless of canvas position in viewport.
            const startPatient = view.clientToPatientCoordinates(
                vec2.fromValues(startMouse.x, startMouse.y)
            );
            const endPatient = view.clientToPatientCoordinates(
                vec2.fromValues(startMouse.x + dx, startMouse.y + dy)
            );
            const patientDelta = vec3.subtract(
                vec3.create(),
                endPatient,
                startPatient
            );

            const newCoords = vec3.add(
                vec3.create(),
                startPatientCoords,
                patientDelta
            );

            domainEventBus.publish(
                new RoiPointCoordinatesChangeRequestedEvent(
                    roiNumber,
                    {
                        x: newCoords[0],
                        y: newCoords[1],
                        z: newCoords[2],
                    },
                    'user'
                )
            );

            dragRef.current = null;
            setDragOffset(null);
            ToolManager.setExternalCrosshairMode(
                poiMoveActive ? CrosshairInteractionMode.RootsOnly : null
            );
        },
        [view, poiMoveActive]
    );

    const startDrag = useWindowMouseDrag(onDrag, onEnd);

    const beginDrag = useCallback(
        (e: React.MouseEvent, roi: RoiReadModel) => {
            if (!poiMoveActive || roi.roiNumber !== selectedRoiNumber) {
                return;
            }
            const pt = roi.pointCoordinatesMm;
            if (!pt) {
                return;
            }
            dragRef.current = {
                roiNumber: roi.roiNumber,
                startMouse: { x: e.clientX, y: e.clientY },
                startPatientCoords: vec3.fromValues(pt[0], pt[1], pt[2]),
            };
            ToolManager.setExternalCrosshairMode(
                CrosshairInteractionMode.DrawingStroke
            );
            startDrag(e);
        },
        [startDrag, poiMoveActive, selectedRoiNumber]
    );

    // True when a point-type ROI is selected but has no coordinates yet —
    // enables click-to-place mode on the viewport.
    const isReadyToPlace = useMemo(() => {
        if (!structureSet || selectedRoiNumber === -1) {
            return false;
        }
        const roi = structureSet.roiArray.find(
            (r) => r.roiNumber === selectedRoiNumber
        );
        return (
            roi !== undefined &&
            ROI_POINT_TYPES.includes(roi.interpretedType) &&
            roi.pointCoordinatesMm === null
        );
    }, [structureSet, selectedRoiNumber]);

    const handlePlaceClick = useCallback(
        (e: React.MouseEvent<SVGRectElement>) => {
            if (!view || selectedRoiNumber === -1) {
                return;
            }
            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(e.clientX, e.clientY)
            );
            domainEventBus.publish(
                new RoiPointCoordinatesChangeRequestedEvent(
                    selectedRoiNumber,
                    {
                        x: patientCoords[0],
                        y: patientCoords[1],
                        z: patientCoords[2],
                    },
                    'user'
                )
            );
        },
        [view, selectedRoiNumber]
    );

    const projectedPois = useMemo(() => {
        if (!view || !structureSet) {
            return [];
        }
        const pointRois = structureSet.roiArray.filter(
            (roi) =>
                ROI_POINT_TYPES.includes(roi.interpretedType) &&
                roi.visible &&
                roi.pointCoordinatesMm != null
        );
        return computeProjectedPois(view, pointRois, selectedRoiNumber);
        // renderTick triggers recomputation when the view changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, structureSet, selectedRoiNumber, renderTick]);

    if (projectedPois.length === 0 && !isReadyToPlace) {
        return null;
    }

    // Selected POI must render last so it sits on top in SVG z-order,
    // ensuring its hit area and drag handle take precedence when markers overlap.
    const selectedPoi = projectedPois.find(
        (p) => p.roi.roiNumber === selectedRoiNumber
    );
    const poisInRenderOrder = selectedPoi
        ? [
              ...projectedPois.filter(
                  (p) => p.roi.roiNumber !== selectedRoiNumber
              ),
              selectedPoi,
          ]
        : projectedPois;

    return (
        <svg
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 7,
            }}
        >
            <style>{`
                .poi-label {
                    opacity: 0;
                    transition: opacity 0.15s;
                    pointer-events: none;
                }
                .poi-group:hover .poi-label {
                    opacity: 1;
                }
                .poi-label-visible {
                    opacity: 1;
                    pointer-events: none;
                }
            `}</style>
            {isReadyToPlace && (
                <rect
                    x={0}
                    y={0}
                    width="100%"
                    height="100%"
                    fill="transparent"
                    style={{ pointerEvents: 'auto', cursor: 'crosshair' }}
                    onClick={handlePlaceClick}
                />
            )}
            {poisInRenderOrder.map((poi) => {
                const { roi, canvasX, canvasY, distanceMm } = poi;
                const isDragging = dragOffset?.roiNumber === roi.roiNumber;
                const displayX = isDragging ? canvasX + dragOffset.dx : canvasX;
                const displayY = isDragging ? canvasY + dragOffset.dy : canvasY;
                const isOnPlane = distanceMm < ON_PLANE_THRESHOLD_MM;
                const color = formatColorRgb(roi.color);
                const fillColor = `rgba(${roi.color.R}, ${roi.color.G}, ${roi.color.B}, ${isOnPlane ? 0.3 : 0.1})`;
                const isMovable =
                    poiMoveActive && roi.roiNumber === selectedRoiNumber;
                const dragCursor = isMovable
                    ? isDragging
                        ? 'grabbing'
                        : 'move'
                    : 'default';

                const isSelected = roi.roiNumber === selectedRoiNumber;
                const groupOpacity = isOnPlane ? 1 : 0.4;

                return (
                    <g
                        key={roi.roiNumber}
                        className="poi-group"
                        opacity={groupOpacity}
                    >
                        {/* Invisible larger hit area for hover + click-to-select */}
                        <circle
                            cx={displayX}
                            cy={displayY}
                            r={ON_PLANE_RADIUS_PX + 4}
                            fill="transparent"
                            stroke="none"
                            style={{
                                pointerEvents: 'auto',
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                domainEventBus.publish(
                                    new RoiSelectionRequestedEvent(
                                        roi.roiNumber,
                                        'user'
                                    )
                                )
                            }
                        />
                        <circle
                            cx={displayX}
                            cy={displayY}
                            r={ON_PLANE_RADIUS_PX}
                            fill={fillColor}
                            stroke={color}
                            strokeWidth={1.5}
                            strokeDasharray={isOnPlane ? 'none' : '2 2'}
                            style={{
                                pointerEvents: isMovable ? 'auto' : 'none',
                                cursor: dragCursor,
                            }}
                            onMouseDown={(e) => beginDrag(e, roi)}
                        />
                        {/* Cross marker */}
                        <line
                            x1={displayX - CROSS_ARM_PX}
                            y1={displayY}
                            x2={displayX + CROSS_ARM_PX}
                            y2={displayY}
                            stroke={color}
                            strokeWidth={1.5}
                        />
                        <line
                            x1={displayX}
                            y1={displayY - CROSS_ARM_PX}
                            x2={displayX}
                            y2={displayY + CROSS_ARM_PX}
                            stroke={color}
                            strokeWidth={1.5}
                        />
                        {/* Name label: always visible when selected, shown on hover otherwise */}
                        <text
                            x={displayX + ON_PLANE_RADIUS_PX + 4}
                            y={displayY + 4}
                            fill={color}
                            fontSize={14}
                            fontFamily="sans-serif"
                            className={
                                isSelected ? 'poi-label-visible' : 'poi-label'
                            }
                            style={{
                                textShadow: '1px 1px 4px black',
                                userSelect: 'none',
                            }}
                        >
                            {roi.name}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

export default PointsOfInterestOverlay;
